import { useCallback, useMemo } from 'react';
import type { TourSequence } from '../types';
import { TOUR_SEQUENCES } from '../tourSequences';
import { useTourPersistence } from './useTourPersistence';

interface UseTourSequenceReturn {
  activeSequence: TourSequence | null;
  isInSequence: boolean;
  currentTourId: string | null;
  currentTourIndex: number;
  startSequence: (sequenceId: string) => void;
  advance: () => string | null;
  cancel: () => void;
}

/**
 * Manages tour sequence state machine.
 *
 * A sequence is an ordered list of tours that play one after another.
 * State is persisted via useTourPersistence (localStorage).
 */
export function useTourSequence(persistence: ReturnType<typeof useTourPersistence>): UseTourSequenceReturn {
  const { state, setActive, clearActive, markTourCompleted, resetTour, setWelcomeSeen, setSkippedAll } = persistence;

  const activeSequence = useMemo<TourSequence | null>(() => {
    const seqId = state.active?.sequenceId;
    if (!seqId) return null;
    return TOUR_SEQUENCES[seqId] ?? null;
  }, [state.active?.sequenceId]);

  const isInSequence = activeSequence !== null;

  const currentTourId = state.active?.tourId ?? null;
  const currentTourIndex = state.active?.tourIndex ?? 0;

  const startSequence = useCallback(
    (sequenceId: string) => {
      const sequence = TOUR_SEQUENCES[sequenceId];
      if (!sequence || sequence.tours.length === 0) return;

      const firstTourId = sequence.tours[0];

      // Reset completion for all tours in the sequence so they replay
      for (const tourId of sequence.tours) {
        resetTour(tourId);
      }

      // Ensure welcome modal won't block
      if (!state.hasSeenWelcome) {
        setWelcomeSeen();
      }

      // Reset skipped preference
      if (state.hasSkippedAll) {
        setSkippedAll(false);
      }

      setActive({
        sequenceId,
        tourId: firstTourId,
        tourIndex: 0,
        stepIndex: 0,
      });
    },
    [state.hasSeenWelcome, state.hasSkippedAll, resetTour, setActive, setWelcomeSeen, setSkippedAll]
  );

  const advance = useCallback((): string | null => {
    if (!activeSequence || !state.active) {
      return null;
    }

    const nextIndex = state.active.tourIndex + 1;

    if (nextIndex < activeSequence.tours.length) {
      // Move to next tour in sequence
      const nextTourId = activeSequence.tours[nextIndex];

      // Reset the next tour so it plays fresh
      resetTour(nextTourId);

      setActive({
        sequenceId: activeSequence.id,
        tourId: nextTourId,
        tourIndex: nextIndex,
        stepIndex: 0,
      });

      return nextTourId;
    }

    // Sequence complete — do NOT mark tours here; the callback handles that.
    // Just clear the active position.
    clearActive();
    return null;
  }, [activeSequence, state.active, resetTour, setActive, clearActive]);

  const cancel = useCallback(() => {
    if (state.active?.tourId) {
      markTourCompleted(state.active.tourId);
    }
    clearActive();
  }, [state.active?.tourId, markTourCompleted, clearActive]);

  return {
    activeSequence,
    isInSequence,
    currentTourId,
    currentTourIndex,
    startSequence,
    advance,
    cancel,
  };
}
